import { useQuery } from "@apollo/client";
import {
  GetMeetingNewPageQueryQuery,
  GetMeetingNewPageQueryQueryVariables,
} from "types/graphql-schema";

import MeetingViewSidebar from "@apps/meeting-new/components/meeting-sidebar/meeting-view-sidebar";
import getMeetingQuery from "@apps/meeting-new/graphql/get-meeting-query";
import Layout from "@components/layout/layout";
import Loading from "@components/loading/loading";

// The profile 1-on-1 tab shows the same sidebar as the 1-on-1 meeting page
// (My Notes, Coaching Insights, Work History, Ask AI), scoped to the focused
// meeting. We reuse MeetingViewSidebar directly so the two stay in sync.
const ProfileOneononeSidebar = ({
  meetingId,
  profileId,
  loadingText,
  useRollingWorkHistoryDateRange,
}: {
  meetingId: number | null;
  profileId: number;
  loadingText: string;
  useRollingWorkHistoryDateRange: boolean;
}) => {
  const { data } = useQuery<
    GetMeetingNewPageQueryQuery,
    GetMeetingNewPageQueryQueryVariables
  >(getMeetingQuery, {
    variables: { meetingId: meetingId ?? -1 },
    skip: meetingId === null,
  });
  const meeting = data?.meeting;

  // Whether the loaded meeting is one of this profile's 1-on-1s. Use the same
  // rule as the list/validator in profile-oneonone-tab.tsx: a formal 1-on-1
  // where the profile user is a participant and is NOT the facilitator. Do NOT
  // compare against `meeting.subject`: for a 1-on-1 with no facilitator it
  // resolves to whichever participant sorts first (often the OTHER person), so
  // the sidebar would load forever for the profile that isn't that subject.
  const meetingGroup = meeting?.meetingGroup;
  const profileIsParticipant = !!meeting?.participants?.edges?.some(
    (edge) => edge?.node?.user?.id === profileId,
  );
  const meetingBelongsToProfile =
    !!meeting &&
    !!meetingGroup?.isFormalOneonone &&
    profileIsParticipant &&
    meetingGroup.facilitator?.id !== profileId;

  if (!meetingBelongsToProfile) {
    return (
      <Layout.Sidebar sidebarExpandedUiPreferenceKey="profileSidebarExpanded">
        <Loading className="mt-6">{loadingText}</Loading>
      </Layout.Sidebar>
    );
  }

  return (
    <MeetingViewSidebar
      meeting={meeting}
      sidebarExpandedUiPreferenceKey="profileSidebarExpanded"
      useRollingWorkHistoryDateRange={useRollingWorkHistoryDateRange}
    />
  );
};

export default ProfileOneononeSidebar;
